import { totalCountNodes, updateTreeNodes } from '@AMIEWEB/Common/TreeView/TreeViewHelpers';
import { candidateSearchFilterName } from '@AMIEWEB/GlobalSearch/Candidate/SearchUtils';
import {
  dropDownFilterOptions,
  getFacilitySearchLookups,
  getFilteredOptions,
  getMultiSelectOptions,
  getOrderIdSearchLookups,
} from '@AMIEWEB/GlobalSearch/helper';
import { AutocompleteChangeReason } from '@mui/material';
import { ISelection } from 'app/ComponentLibrary/Filter/CustomSelect';
import { FilterCompTypes } from 'app/ComponentLibrary/Filter/utils';
import {
  getAllCitiesAndZips,
  getFilteredCities,
  getFilteredZips,
  upgradeBasedOnSearchOption,
} from 'app/components/GlobalSearch/Candidate/FilterUtils';
import { FilterType } from 'app/enums/Common';
import { getSortedData, getUnique } from 'app/helpers/arrayHelpers';
import { IStateCityZip } from 'app/models/Candidate/IStateCityZip';
import {
  fetchDisciplineAndSpecialty,
  getCitiesAndZipsByStates,
  getSearchFilterOptions,
  getSearchLookups,
} from 'app/services/SharedServices/SharedServices';
import i18next from 'i18next';
import { initialFilterChips, IOrderMatchCips } from '../store/candidateOrderMatch.redux';
import {
  prependOrderSearchBlankOption,
  orderSearchBlankMultiSelectHandleChange,
  excludeOrderSearchBlankFromOptions,
} from '@AMIEWEB/GlobalSearch/Order/orderSearchBlank';

export enum OrderMatchFlterName {
  division = 'divisionIds',
  businessRelationship = 'businessRelationshipIds',
  facilityStatus = 'facilityStatusIds',
  orderType = 'orderTypeIds',
  skillsetFilter = 'skillsetFilter',
  discipline = 'disciplineIds',
  specialty = 'specialtyIds',
  subSpecialty = 'subSpecialtyIds',
  state = 'states',
  city = 'city',
  zip = 'zipCode',
  radius = 'radius',
  facility = 'facilityIds',
  mspClient = 'mspClientIds',
  contractAffiliates = 'affiliateIds',
  orderAgeDateRange = 'dateEnteredRange',
  orderEndDateRange = 'endDateRange',
  billRate = 'billrate',
  paysGww = 'paysGww',
  guaranteedHours = 'guaranteedHours',
  expectedHours = 'expectedHours',
  reason = 'reasonids',
  education = 'education',
  accountManager = 'amUserIds',
  orderPriority = 'orderPriorityIds',
  shift = 'shiftIds',
  weeklyGrossPay = 'weeklyGrossPay',
  startDateRange = 'orderStartDateRange',
  mfs = 'maxFilesSentId',
  positions = 'positions',
  vaccinated = 'vaccinatedIds',
  orderIds = 'orderIds',
  region = 'region',
  staffingVendor = 'staffingVendorIds',
  techVendor = 'technologyVendorIds',
  orderStartDateRangeIsBlank = 'orderStartDateRangeIsBlank',
  orderEndDateRangeIsBlank = 'endDateRangeIsBlank',
}
export const EducationOpts = [
  { id: 0, value: 'clinicalFellowshipYear', name: 'Clinical Fellow', labelPrefix: 'Education' },
  { id: 1, value: 'newGrad', name: 'New Grad', labelPrefix: 'Education' },
];
export const MFSOpts = [
  { id: 0, value: 1, name: 'Yes', labelPrefix: 'MFS' },
  { id: 1, value: 2, name: 'No', labelPrefix: 'MFS' },
];
export const PaysGwwOpts = [
  { id: 0, value: true, name: 'Yes', labelPrefix: 'GWW' },
  { id: 1, value: false, name: 'No', labelPrefix: 'GWW' },
];
export const VaccinatedOpts = [
  { id: 0, value: 1, name: 'Yes', labelPrefix: 'Vaccinated' },
  { id: 1, value: 2, name: 'No', labelPrefix: 'Vaccinated' },
];

export const PositionOpts = [
  {
    id: 0,
    value: { min: 0, max: 0 },
    name: '0',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 1,
    value: { min: 1, max: 999 },
    name: '>0',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 2,
    value: { min: 1, max: 10 },
    name: '1-10',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 3,
    value: { min: 10, max: 50 },
    name: '10-50',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 4,
    value: { min: 50, max: 99 },
    name: '50-99',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
  {
    id: 5,
    value: { min: 100, max: 999 },
    name: '>99',
    labelPrefix: i18next.t('search.globalSearch.order.filterNames.positions'),
  },
] as unknown as ISelection[];

export class OrderMatchSearchFilters {
  constructor(public filterSpecs: any, public gridStateFilters: any) {}

  getOrderMatchFilters = () => {
    const date = new Date();
    return [
      {
        filterName: i18next.t('home.pricingWorkDesk.filterHeading.OID'),
        name: OrderMatchFlterName.orderIds,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        fetchOptions: getOrderIdSearchLookups,
        isMultiSelect: true,
        applyOnClickAway: true,
        searchOnCharacterCount: 1,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.division'),
        name: OrderMatchFlterName.division,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        isSelectAll: true,
        options: this.filterSpecs?.divisionOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.businessRelationship'),
        name: OrderMatchFlterName.businessRelationship,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        isSelectAll: true,
        options: this.filterSpecs.businessRelationshipsOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.facilityStatus'),
        name: OrderMatchFlterName.facilityStatus,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        isSelectAll: true,
        options: this.filterSpecs.facilityStatusesOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.type'),
        name: OrderMatchFlterName.orderType,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        isSelectAll: true,
        options: this.filterSpecs.orderTypesOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.candidate.filterNames.skillSet'),
        name: candidateSearchFilterName.candidateSkillSet,
        type: FilterCompTypes.CANDIDATESKILLSET,
        isMultiSelect: true,
        options: [],
        placeholder: 'Select',
        version2: true,
        returnsObjectAsValue: true,
        size: 'large',
        isExact: true,
        customSelectedCount: value => totalCountNodes(value),
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.state'),
        name: OrderMatchFlterName.state,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        isSelectAll: true,
        options: this.filterSpecs.statesOpts,
        compactValues: this.filterSpecs.compactStates,
        handleChangeCallback: (newValue = [], reason: AutocompleteChangeReason, { option }, onChange) => {
          if (option.value === 'All') {
            onChange(reason === 'selectOption' ? this.filterSpecs.statesOpts : []);
            return;
          }
          const compactValuesSelected = newValue.reduce(
            (results, item) => (item.isCompactOpt ? [...results, ...item.value] : results),
            [],
          );
          let newSelections = this.filterSpecs.statesOpts.reduce((results, item) => {
            let isItemSelected =
              newValue.some(x => x.value === item.value) || compactValuesSelected.some(x => x.value === item.value);

            return isItemSelected ? [...results, item] : results;
          }, []);

          if (reason === 'removeOption' && option.isCompactOpt) {
            newSelections = newSelections.reduce(
              (results, item) => (option.value.some(opt => opt.name === item.name) ? results : [...results, item]),
              [],
            );
            const compactValuesPresent = newSelections.filter(item => item?.isCompactOpt === true);
            compactValuesPresent.forEach(opt => {
              newSelections = [...newSelections, ...opt.value];
            });
            const uniqueValues = newSelections.filter((val, index, self) => {
              return self.findIndex(p => p.value === val.value) === index;
            });
            newSelections = uniqueValues;
          } else if (reason === 'removeOption' && !option.isCompactOpt) {
            const parentIndex = newSelections.findIndex(
              item => item.isCompactOpt && item.value.some(x => x.value === option.value),
            );
            parentIndex !== -1 && newSelections.splice(parentIndex, 1);

            const childIndex = newSelections.findIndex(item => item.value === option.value);
            childIndex !== -1 && newSelections.splice(childIndex, 1);
          }

          onChange(newSelections);
        },
        filterOptions: (options, { inputValue }) => {
          return dropDownFilterOptions(options, inputValue);
        },
      },
      {
        filterName: i18next.t('search.globalSearch.candidate.filterNames.city'),
        name: OrderMatchFlterName.city,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        fetchOptions: async (key: string) => {
          if (!key) return getFilteredCities(this.filterSpecs.cityOptions, key);
          const cityOptions = await upgradeBasedOnSearchOption(key, 'c', this.gridStateFilters);
          return cityOptions;
        },
        isMultiSelect: true,
        isSelectAll: true,
      },
      {
        filterName: i18next.t('search.globalSearch.candidate.filterNames.zip'),
        name: OrderMatchFlterName.zip,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        fetchOptions: async (key: string) => {
          if (!key) return getFilteredZips(this.filterSpecs.zipOptions, key);
          const zipOptions = await upgradeBasedOnSearchOption(key, 'z', this.gridStateFilters);
          return zipOptions;
        },
        isMultiSelect: false,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.radius'),
        name: OrderMatchFlterName.radius,
        type: FilterCompTypes.TEXTFIELDNUMBER,
        placeholder: 'Radius',
        disabled: this.gridStateFilters?.filters?.zipCode ? false : true,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.facility'),
        name: OrderMatchFlterName.facility,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        fetchOptions: getFacilitySearchLookups,
        isMultiSelect: true,
        isSelectAll: true,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.mspClient'),
        name: OrderMatchFlterName.mspClient,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        options: this.filterSpecs.mspClientOpts,
        isMultiSelect: true,
        isSelectAll: true,
        handleChangeCallback: orderSearchBlankMultiSelectHandleChange,
        applySelectAllFilter: excludeOrderSearchBlankFromOptions,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.contractAffiliates'),
        name: OrderMatchFlterName.contractAffiliates,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        fetchOptions: async key =>
          prependOrderSearchBlankOption(await getSearchLookups('contractaffiliates', key)),
        handleChangeCallback: orderSearchBlankMultiSelectHandleChange,
        applySelectAllFilter: excludeOrderSearchBlankFromOptions,
        isMultiSelect: true,
        isSelectAll: true,
      },

      {
        filterName: i18next.t('search.globalSearch.order.filterNames.orderAgeDateRange'),
        name: OrderMatchFlterName.orderAgeDateRange,
        placeholder: i18next.t('search.globalSearch.order.filterNames.orderAgeDateRange'),
        version3: true,
        type: FilterCompTypes.DATERANGEV2,
        variant: 'outlined',
        secondaryLabel: i18next.t('search.globalSearch.order.filterNames.orderAgeDateRange'),
        useMaxWidth: true,
        maxWidth: 210,
        clickAway: true,
        disablePortal: true,
        quickSelect: false,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.orderEndDateRange'),
        name: OrderMatchFlterName.orderEndDateRange,
        placeholder: i18next.t('search.globalSearch.order.filterNames.orderEndDateRange'),
        version3: true,
        type: FilterCompTypes.DATERANGEV2,
        variant: 'outlined',
        secondaryLabel: i18next.t('search.globalSearch.order.filterNames.orderEndDateRange'),
        useMaxWidth: true,
        maxWidth: 210,
        clickAway: true,
        disablePortal: true,
        quickSelect: false,
        supportsBlankDateFilter: true,
        blankFieldName: OrderMatchFlterName.orderEndDateRangeIsBlank,
        blankCheckboxLabel: 'Blank',
      },
      {
        filterName: i18next.t('Bill Rate'),
        name: OrderMatchFlterName.billRate,
        type: FilterCompTypes.NUMBERRANGE,
        placeholder: i18next.t('Bill Rate'),
        numberRangeType: 'currency',
      },

      {
        filterName: i18next.t('search.globalSearch.order.filterNames.paysGww'),
        name: OrderMatchFlterName.paysGww,
        placeholder: i18next.t('search.globalSearch.order.filterNames.paysGww'),
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        options: PaysGwwOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.guaranteedHours'),
        name: OrderMatchFlterName.guaranteedHours,
        type: FilterCompTypes.NUMBERRANGE,
        placeholder: i18next.t('search.globalSearch.order.filterNames.guaranteedHours'),
        numberRangeType: 'hours',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.expectedHours'),
        name: OrderMatchFlterName.expectedHours,
        type: FilterCompTypes.NUMBERRANGE,
        placeholder: i18next.t('search.globalSearch.order.filterNames.expectedHours'),
        numberRangeType: 'hours',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.reason'),
        name: OrderMatchFlterName.reason,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        options: this.filterSpecs.orderReasonOpts,
        isMultiSelect: true,
        isSelectAll: true,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.education'),
        name: OrderMatchFlterName.education,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        options: EducationOpts,
        isMultiSelect: true,
        isSelectAll: true,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.accountManager'),
        name: OrderMatchFlterName.accountManager,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        isSelectAll: true,
        options: this.filterSpecs.accountManagerOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.orderPriority'),
        name: OrderMatchFlterName.orderPriority,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        isSelectAll: true,
        options: this.filterSpecs.orderPrioritiesOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.shift'),
        name: OrderMatchFlterName.shift,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        isSelectAll: true,
        options: this.filterSpecs.shiftOpts,
        handleChangeCallback: orderSearchBlankMultiSelectHandleChange,
        applySelectAllFilter: excludeOrderSearchBlankFromOptions,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.weeklyGrossPay'),
        name: OrderMatchFlterName.weeklyGrossPay,
        type: FilterCompTypes.NUMBERRANGE,
        placeholder: 'Weekly Gross',
        numberRangeType: 'currency',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.startDateRange'),
        name: OrderMatchFlterName.startDateRange,
        placeholder: i18next.t('search.globalSearch.order.filterNames.startDateRange'),
        version3: true,
        type: FilterCompTypes.DATERANGEV2,
        variant: 'outlined',
        secondaryLabel: i18next.t('search.globalSearch.order.filterNames.startDateRange'),
        useMaxWidth: true,
        maxWidth: 210,
        clickAway: true,
        disablePortal: true,
        quickSelect: false,
        supportsBlankDateFilter: true,
        blankFieldName: OrderMatchFlterName.orderStartDateRangeIsBlank,
        blankCheckboxLabel: 'Blank',
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.mfs'),
        name: OrderMatchFlterName.mfs,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        options: MFSOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.positions'),
        name: OrderMatchFlterName.positions,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        options: PositionOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.vaccinated'),
        name: OrderMatchFlterName.vaccinated,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        options: VaccinatedOpts,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.region'),
        name: OrderMatchFlterName.region,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        fetchOptions: async key =>
          prependOrderSearchBlankOption(await getFilteredOptions(this.filterSpecs.regionOpts, key)),
        handleChangeCallback: orderSearchBlankMultiSelectHandleChange,
        applySelectAllFilter: excludeOrderSearchBlankFromOptions,
        applyOnClickAway: true,
        applyOnEnter: true,
        isSelectAll: true,
        searchOnCharacterCount: 1,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.staffingVendor'),
        name: OrderMatchFlterName.staffingVendor,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        isSelectAll: true,
        options: this.filterSpecs.staffingVendorOpts,
        handleChangeCallback: orderSearchBlankMultiSelectHandleChange,
        applySelectAllFilter: excludeOrderSearchBlankFromOptions,
      },
      {
        filterName: i18next.t('search.globalSearch.order.filterNames.techVendor'),
        name: OrderMatchFlterName.techVendor,
        type: FilterCompTypes.TYPEAHEADDROPDOWN,
        isMultiSelect: true,
        isSelectAll: true,
        options: this.filterSpecs.techVendorOpts,
        handleChangeCallback: orderSearchBlankMultiSelectHandleChange,
        applySelectAllFilter: excludeOrderSearchBlankFromOptions,
      },
    ];
  };
}

export const convertToTreeStructure = data => {
  const tree = [];

  const disciplineMap = new Map();

  data?.forEach(item => {
    const { disciplineId, disciplineAbbr, specialtyId, specialtyAbbr, subSpecialtyId, subSpecialtyAbbr } = item;

    // Find or create the discipline node
    if (!disciplineMap?.has(disciplineId)) {
      disciplineMap?.set(disciplineId, {
        name: disciplineAbbr,
        value: disciplineId,
        children: [],
        userChecked: true,
      });
      tree.push(disciplineMap?.get(disciplineId));
    }

    const disciplineNode = disciplineMap?.get(disciplineId);

    // Find or create the specialty node
    let specialtyNode = disciplineNode?.children?.find(child => child?.value == `${disciplineId}-${specialtyId}`);
    if (!specialtyNode) {
      specialtyNode = {
        name: specialtyAbbr,
        value: `${disciplineId}-${specialtyId}`,
        children: [],
        userChecked: true,
      };
      disciplineNode?.children?.push(specialtyNode);
    }

    // Add the subspecialty node if it exists
    if (subSpecialtyId !== null && subSpecialtyAbbr !== null) {
      specialtyNode?.children?.push({
        name: subSpecialtyAbbr,
        value: `${disciplineId}-${specialtyId}-${subSpecialtyId}`,
        userChecked: true,
      });
    }
  });

  return tree;
};
export const getSkillSets = defaultSkillsets => {
  let newSkillSetFilter = convertToTreeStructure(defaultSkillsets);
  if (newSkillSetFilter?.length > 0) {
    newSkillSetFilter = updateTreeNodes(newSkillSetFilter);
    newSkillSetFilter[0].filterName = 'skillsetFilter';
    return {
      allowableMismatch: false,
      isVerifiedOnly: false,
      operation: 'Or',
      skillsets: newSkillSetFilter,
    };
  }
};

export const CONSTANT = {
  ORDER_PREFERENCE_KEY: 'orderMatch_candidateInfo',
};
export const manualFilterApply = true;

export const getDefaultAppliedFilters = ({ matchingDivisions, skillset }): IOrderMatchCips => {
  return {
    ...initialFilterChips,
    divisionIds: matchingDivisions,
    specialtyIds: [],
    disciplineIds: [],
    skillsetFilter: skillset,
    maxFilesSentId: MFSOpts[1],
    positions: PositionOpts[1],
  };
};

export const getOrderPreferencePreFilters = ({
  matchingDivisions,
  matchingStateOpts,
  orderPreferenceResult,
  matchingShiftOpts,
  prefSkillset,
}): IOrderMatchCips => {
  return {
    ...initialFilterChips,
    divisionIds: matchingDivisions,
    specialtyIds: [],
    disciplineIds: [],
    maxFilesSentId: MFSOpts[1],
    positions: PositionOpts[1],
    states: matchingStateOpts,
    city: orderPreferenceResult?.city,
    zipCode: orderPreferenceResult?.zip,
    orderStartDateRange: orderPreferenceResult?.orderStartDateRange,
    billrate: orderPreferenceResult?.billRate
      ? { min: orderPreferenceResult?.billRate[0], max: orderPreferenceResult?.billRate[1] }
      : null,
    education: orderPreferenceResult?.education,
    shiftIds: matchingShiftOpts,
    radius: orderPreferenceResult?.radius?.toString(),
    skillsetFilter: prefSkillset,
  };
};
const getDisciplinesMultiSelectOptions = options => {
  const array = options?.reduce(
    (resultArray, item) => [
      ...resultArray,
      {
        value: item?.value?.slice(item?.value?.indexOf('_') + 1),
        name: item.name,
        corelation: item.value,
      },
    ],
    [],
  );
  const sortedData = getSortedData(array || [], FilterType.string);
  return sortedData;
};

interface FilterSpecsParams {
  setAllSpecialities: (data: any) => void;
  setAllSubSpecialities: (data: any) => void;
  allCitiesandZipsData: React.MutableRefObject<IStateCityZip[] | undefined>;
  gridSpecs: React.MutableRefObject<any>;
  setFields: (fields: any) => void;
}

export const fetchFilterSpecs = async (key: string, params: FilterSpecsParams) => {
  const {
    // getDisciplinesMultiSelectOptions,
    setAllSpecialities,
    setAllSubSpecialities,
    allCitiesandZipsData,
    gridSpecs,
    setFields,
  } = params;

  // Fetch all lookups in parallel
  const [
    divisionsLookups,
    businessRelationshipsLookups,
    facilitysStatusesLookups,
    orderTypesLookups,
    disciplines,
    speciality,
    subSpecialtiesLookups,
    statesLookups,
    compactStatesOpts,
    facilityLookups,
    mspClientLookups,
    orderReasonLookups,
    accountManagerLookups,
    orderPrioritiesLookups,
    shiftLookups,
    regionLookups,
    staffingVendorLookups,
    technologyVendorLookups,
  ] = await Promise.all([
    getSearchFilterOptions('divisions'),
    getSearchFilterOptions('businessrelationships'),
    getSearchFilterOptions('facilitystatuses'),
    getSearchFilterOptions('ordertypes'),
    getSearchFilterOptions('disciplines'),
    getSearchFilterOptions('discipline_specialties'),
    getSearchFilterOptions('specialty_subspecialties'),
    getSearchFilterOptions('states'),
    getSearchFilterOptions('compact_states'),
    getSearchFilterOptions('facility'),
    getSearchFilterOptions('mspclients'),
    getSearchFilterOptions('orderreason'),
    getSearchFilterOptions('accountmanagers'),
    getSearchFilterOptions('orderpriorities'),
    getSearchFilterOptions('shiftlist'),
    getSearchFilterOptions('orderplacementregions'),
    getSearchFilterOptions('staffingVendor'),
    getSearchFilterOptions('technologyVendor'),
  ]);

  const states = getMultiSelectOptions(statesLookups || []);

  const compactStates = compactStatesOpts?.map(item => {
    const stateAbbrs = item.name?.split(',');
    return {
      name: item?.value?.replace('?', "'")?.replace('Only ', '')?.replace('LIC', 'Licence'),
      value: states.filter(s => stateAbbrs.includes(s.value)),
      isCompactOpt: true,
    };
  });

  const allSpecialtiesOpts = getMultiSelectOptions(getDisciplinesMultiSelectOptions(speciality || []));
  const specialityOpts = getUnique(
    allSpecialtiesOpts?.filter(x => x.name),
    'name',
  );
  setAllSpecialities(allSpecialtiesOpts);

  const allSubSpecialtiesOpts = getMultiSelectOptions(getDisciplinesMultiSelectOptions(subSpecialtiesLookups || []));
  setAllSubSpecialities(allSubSpecialtiesOpts);
  const subSpecialtiesOpts = getUnique(
    allSubSpecialtiesOpts?.filter(x => x.name),
    'name',
  );

  allCitiesandZipsData.current = (await getCitiesAndZipsByStates({
    states: [statesLookups?.[0]?.name],
    cities: undefined,
  })) as IStateCityZip[];

  const AllCitiesAndZips = getAllCitiesAndZips(allCitiesandZipsData.current);

  const filterSpecs = {
    divisionOpts: getMultiSelectOptions(divisionsLookups || []),
    businessRelationshipsOpts: getMultiSelectOptions(businessRelationshipsLookups || []),
    facilityStatusesOpts: getMultiSelectOptions(facilitysStatusesLookups || []),
    orderTypesOpts: getMultiSelectOptions(orderTypesLookups || []),
    disciplineOpts: getMultiSelectOptions(disciplines || []),
    specialityOpts,
    subSpecialtiesOpts,
    statesOpts: getMultiSelectOptions([...compactStates, ...statesLookups]),
    compactStates,
    facilityOpts: getMultiSelectOptions(facilityLookups || []),
    mspClientOpts: prependOrderSearchBlankOption(getMultiSelectOptions(mspClientLookups || [])),
    orderReasonOpts: getMultiSelectOptions(orderReasonLookups || []),
    accountManagerOpts: getMultiSelectOptions(accountManagerLookups || []),
    orderPrioritiesOpts: getMultiSelectOptions(orderPrioritiesLookups || []),
    shiftOpts: prependOrderSearchBlankOption(getMultiSelectOptions(shiftLookups || [])),
    regionOpts: prependOrderSearchBlankOption(getMultiSelectOptions(regionLookups || [], { withoutAllOption: true })),
    staffingVendorOpts: prependOrderSearchBlankOption(getMultiSelectOptions(staffingVendorLookups || [])),
    techVendorOpts: prependOrderSearchBlankOption(getMultiSelectOptions(technologyVendorLookups || [])),
    cityOptions: AllCitiesAndZips?.cities,
    zipOptions: AllCitiesAndZips?.zips,
  };

  gridSpecs.current.filterSpecs = filterSpecs;
  setFields(gridSpecs.current.getOrderMatchFilters());
};

export const getDisciplineAndSpecialty = async (disciplineIds, specialtyIds) => {
  let newSkillSetFilter = [];

  const abortController = new AbortController();
  const disciplineAndSpecialty = (await fetchDisciplineAndSpecialty('', abortController.signal, true)) as any;

  if (disciplineAndSpecialty?.treeStructure?.length > 0) {
    const skillSetTree = disciplineAndSpecialty?.treeStructure;

    //If Specialty array is not empty, then pick only the valid and active values of Discipline and Specialty combination.
    if (specialtyIds?.length > 0) {
      for (const disciplineId of disciplineIds) {
        let specialtyChildren = [];
        let node = skillSetTree?.find(t => t.value == disciplineId?.value);
        if (node) {
          for (const specialtyId of specialtyIds) {
            const specialtyNode = node?.children?.find(child => child?.value == `${node?.value}-${specialtyId?.value}`);

            //If Specialty is not present under Discipline, then don't pick anything at all.
            if (specialtyNode) {
              specialtyNode.userChecked = true;
              specialtyChildren = [...specialtyChildren, { ...specialtyNode, children: [] }];
            }
          }
        }

        if (specialtyChildren?.length > 0) {
          let disciplineNode = {
            name: node?.name,
            value: node?.value,
            userChecked: true,
            children: specialtyChildren,
          };
          newSkillSetFilter = [...newSkillSetFilter, disciplineNode];
        }
      }
    }
    //If Specialty array is empty, then pick only the valid and active values of Discipline.
    else {
      for (const disciplineId of disciplineIds) {
        let node = skillSetTree?.find(t => t.value == disciplineId?.value);
        if (node) {
          node.userChecked = true;
          newSkillSetFilter = [...newSkillSetFilter, { ...node, children: [] }];
        }
      }
    }
  }

  let skillSets = null;
  if (newSkillSetFilter?.length > 0) {
    newSkillSetFilter[0].filterName = 'skillsetFilter';
    skillSets = {
      allowableMismatch: false,
      isVerifiedOnly: false,
      operation: 'Or',
      skillsets: updateTreeNodes(newSkillSetFilter),
    };
  }
  return skillSets;
};

export const findspecialities = ({ disciplines, gridSpecs, allSpecialities, setFields, allSubSpecialities }) => {
  const specialtiesArray: any[] = [];
  if (disciplines?.length > 0) {
    disciplines?.forEach(item => {
      const specialties = allSpecialities.filter(
        curr => curr?.corelation?.slice(0, curr?.corelation?.indexOf('_')) === item?.value,
      );
      if (specialties) {
        specialtiesArray.push(...specialties);
      }
    });
    const sortedData = getSortedData(specialtiesArray, FilterType.string);
    const uniqueSpecialities = getUnique(
      sortedData?.filter(x => x.name),
      'name',
    );
    if (uniqueSpecialities?.length >= 1) {
      gridSpecs.current.filterSpecs.specialityOpts = getMultiSelectOptions(uniqueSpecialities);
      const fieldsSchema = gridSpecs.current.getOrderMatchFilters();
      setFields(fieldsSchema);
      findSubSpecialities({ specialities: specialtiesArray, gridSpecs, allSubSpecialities, setFields });
    } else {
      gridSpecs.current.filterSpecs.subSpecialtiesOpts = [];
      gridSpecs.current.filterSpecs.specialityOpts = getMultiSelectOptions(uniqueSpecialities);
      const fieldsSchema = gridSpecs.current.getOrderMatchFilters();
      setFields(fieldsSchema);
    }
  } else {
    gridSpecs.current.filterSpecs.specialityOpts = getUnique(
      allSpecialities?.filter(x => x.name),
      'name',
    );
    const fieldsSchema = gridSpecs.current.getOrderMatchFilters();
    setFields(fieldsSchema);
    findSubSpecialities({ specialities: [], gridSpecs, allSubSpecialities, setFields });
  }
};

export const findSubSpecialities = ({ specialities, gridSpecs, allSubSpecialities, setFields }) => {
  const subSpecialtiesArray: any[] = [];
  if (specialities?.length > 0) {
    specialities?.forEach(item => {
      allSubSpecialities?.forEach(curr => {
        if (
          curr?.corelation?.substr(0, curr?.corelation?.lastIndexOf('_')) ===
          item?.corelation?.slice(item?.corelation?.indexOf('_') + 1)
        ) {
          if (curr?.name !== 'All') {
            subSpecialtiesArray.push(curr);
          }
        }
      });
    });
    const sortedData = getSortedData(subSpecialtiesArray, FilterType.string);
    const uniqueSubSpecialities = getUnique(
      sortedData?.filter(x => x.name),
      'name',
    );
    gridSpecs.current.filterSpecs.subSpecialtiesOpts = getMultiSelectOptions(uniqueSubSpecialities);
    const fieldsSchema = gridSpecs.current.getOrderMatchFilters();
    setFields(fieldsSchema);
  } else {
    gridSpecs.current.filterSpecs.subSpecialtiesOpts = getUnique(
      allSubSpecialities?.filter(x => x.name),
      'name',
    );
  }
};
